import { cloneDeep } from 'lodash-es';
import { useBetFiltersValidation } from '@/composables/useBetFiltersValidation';
import { BetDateType, UserAbility } from '@/constants';
import { todayDateRangeInUserTimeZone } from '@/helpers';
import { hasAccess } from '@/helpers/access';
import { runBetExport } from '@/helpers/betExport';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { BetItem } from '@/models/bet';
import { getTableHeaders } from '@/pages/admin/bets/BetsPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { betService } from '@/services/betService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IBetFilters, IBetStats, IPageOptions } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'createdAt', order: 'desc' as const }];

function createBetStoreSetup(storeId: string) {
    // --- state ---
    const { isHandicapValid, isCorrectScoreValid } = useBetFiltersValidation();
    const items = shallowRef<BetItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const isExporting = ref(false);
    const stats = ref<IBetStats | undefined>(undefined);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const allFilters = reactive<IBetFilters>({
        sportId: { ...createRequiredSportFilter(), access: UserAbility.Bet.ViewFilterCommon },
        betDateType: {
            value: BetDateType.PlacementLondon,
            title: 'bets.betDateType',
            allowSaveState: true,
            disableClear: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'bets.betDateFilter',
            allowSaveState: false,
            disableClear: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betSearch: {
            value: null,
            title: 'bets.betSearch',
            allowSaveState: false,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betTradingTypeId: {
            value: null,
            title: 'bets.tradingType',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betBookmakerId: {
            value: null,
            title: 'bets.bookmaker',
            allowSaveState: true,
            access: UserAbility.Bet.ViewBookmaker,
        },
        betStatusId: {
            value: null,
            title: 'bets.status',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betPlatformId: {
            value: null,
            title: 'bets.platform',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFullBetInfo,
        },
        userObserveId: {
            value: null,
            title: 'bets.userObserveId',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFullBetInfo,
        },
        userAgentId: {
            value: null,
            title: 'bets.agent',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFullBetInfo,
        },
        betUserId: {
            value: null,
            title: 'bets.player',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betBotId: {
            value: null,
            title: 'bets.bot',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betAccountId: {
            value: null,
            title: 'bets.account',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFullBetInfo,
        },
        betEventPeriodId: {
            value: null,
            title: 'bets.periodType',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betSelectionId: {
            value: null,
            title: 'bets.betType',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betCustomTeamId: {
            value: null,
            title: 'common.customTeam',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betEventType: {
            value: null,
            title: 'common.marketType',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betHandicap: {
            value: null,
            title: 'filters.handicap',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betCorrectScore: {
            value: null,
            title: 'common.correctScore',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        customLeagueLimitsCategoryId: {
            value: null,
            title: 'pregame.leagueLimitsCategory',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betCustomLeagueId: {
            value: null,
            title: 'common.customLeague',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betOrderId: {
            value: null,
            title: 'orders.orderId',
            allowSaveState: false,
            forApiOnly: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betIP: {
            value: null,
            title: 'proxy.form.ip',
            allowSaveState: false,
            forApiOnly: true,
            access: UserAbility.Bet.ViewFilterCommon,
        },
        betBetVsOpen: {
            value: null,
            title: 'bets.betVsOpen',
            allowSaveState: true,
            access: UserAbility.Bet.ViewBetAdminDetails,
        },
        betsWithMissingStake: {
            value: null,
            title: 'bets.betsWithMissingStake',
            allowSaveState: true,
            access: UserAbility.Bet.ViewFullBetInfo,
        },
        betHasPositionPlatformRule: {
            value: null,
            title: 'bets.betHasPositionPlatformRule',
            allowSaveState: true,
            access: UserAbility.Bet.ViewBetAdminDetails,
        },
        betHasPositionUserRule: {
            value: null,
            title: 'bets.betHasPositionUserRule',
            allowSaveState: true,
            access: UserAbility.Bet.ViewBetAdminDetails,
        },
    });

    const accessibleFilterEntries = Object.entries(allFilters).filter(([, filter]) => hasAccess(filter.access, false));
    const filters = Object.fromEntries(accessibleFilterEntries) as unknown as IBetFilters;

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(Object.fromEntries(accessibleFilterEntries));

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(allFilters.sportId);
        allFilters.betDateType.value = BetDateType.PlacementLondon;
        allFilters.betDate.value = todayDateRangeInUserTimeZone();
        allFilters.betSearch.value = null;
        allFilters.betTradingTypeId.value = null;
        allFilters.betBookmakerId.value = null;
        allFilters.betStatusId.value = null;
        allFilters.betPlatformId.value = null;
        allFilters.userObserveId.value = null;
        allFilters.userAgentId.value = null;
        allFilters.betUserId.value = null;
        allFilters.betBotId.value = null;
        allFilters.betAccountId.value = null;
        allFilters.betEventPeriodId.value = null;
        allFilters.betSelectionId.value = null;
        allFilters.betCustomTeamId.value = null;
        allFilters.betEventType.value = null;
        allFilters.betHandicap.value = null;
        allFilters.betCorrectScore.value = null;
        allFilters.customLeagueLimitsCategoryId.value = null;
        allFilters.betCustomLeagueId.value = null;
        allFilters.betOrderId.value = null;
        allFilters.betIP.value = null;
        allFilters.betsWithMissingStake.value = null;
        allFilters.betHasPositionPlatformRule.value = null;
        allFilters.betHasPositionUserRule.value = null;
        allFilters.betBetVsOpen.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => getTableHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'bet',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        if (!isHandicapValid(filters.betHandicap.value)) return;
        if (!isCorrectScoreValid(filters.betCorrectScore.value)) return;

        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);
            if (filters.betSearch.value) {
                filtersToSend.betDate = { ...filtersToSend.betDate, value: null };
            }

            const params = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: storeId === 'bet' ? ['Stats'] : [],
                requiredDictionaries: ['Agent', 'Country', 'PositionPlatformRule', 'PositionUserRule'],
                requestKey: `bet-list:${storeId}`,
            };
            const response = await betService.list(params);
            items.value = response.data.rows.map((item) => new BetItem(item));
            total.value = response.data.total;
            stats.value = response.data.stats;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    async function exportData() {
        isExporting.value = true;
        try {
            await runBetExport(filters);
        } catch (e) {
            console.error('Export error:', e);
            throw e;
        } finally {
            isExporting.value = false;
        }
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        isExporting,
        options,
        loadData,
        exportData,
        updateOptions,
        clearData,
        stats,
    };
}

export function useBetStore(storeId: string) {
    const useStore = defineStore(storeId, () => createBetStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'bet'),
    });
    return useStore();
}

export type BetStoreSetup = ReturnType<typeof useBetStore>;
